import React, { useEffect, useRef, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import { Icon } from '@this/shared/tooltip/tooltip';
import type { AITTheme } from '@this/shared/ui/theme';
import { useAITTheme } from '@this/shared/ui/theme';
import type { DomesticAirFareDetail } from '@this/domain/domestic_air/fare_detail_master';

interface Props {
  detail: DomesticAirFareDetail;
  place?: 'left';
}

/**
 * 運賃詳細ツールチップの見た目（tooltip_design.png 準拠: 薄グレー背景・黒系文字・幅240px程度）。
 * MUI Tooltip は Popper（Portal）ベースのため、候補一覧のスクロールコンテナや
 * カード境界の overflow でクリップされない。
 */
const StyledTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#eee',
    // AITThemeProvider が MUI テーマに設定するデザイントークンの text.primary
    color: theme.palette.text.primary,
    border: '1px solid #aaa',
    borderRadius: '3px',
    padding: '5px',
    maxWidth: '240px',
    fontWeight: 'normal'
  }
}))(Tooltip);

/**
 * 国内航空券の運賃詳細（ヘッダ=運賃名＋4項目: 予約変更手数料/払い戻し/機内手荷物/預け入れ手荷物＋注記）を
 * ⓘアイコンのホバー（SPはタップ）で表示するツールチップ。
 * 右パネル（候補カード）・左パネル（旅程エリア）共用。レイアウトは tooltip_design.png 準拠。
 *
 * MUI Tooltip はクリック/タッチイベントを止めないため、ラッパで stopPropagation して
 * 候補カードのクリック選択との干渉を防ぐ。
 * 画面外へのはみ出しは Popper の flip/preventOverflow が自動調整する。
 *
 * 表示制御は controlled モードで自前実装: PCはホバーで開閉、SP（タッチ）はタップでトグルし
 * 再タップまで表示を維持する（MUI標準の leaveTouchDelay による自動クローズは使わない）。
 * タッチ後に発火する合成マウスイベントで誤って閉じないよう、タッチ直後の一定時間はマウス系を無視する
 * （タッチ・マウス混在端末でマウスに持ち替えた場合はホバー操作に戻れる）。
 */
const SYNTHETIC_MOUSE_EVENT_IGNORE_MS = 700;

const DomesticAirFareTooltip: React.FC<Props> = ({ detail, place }) => {
  const { theme } = useAITTheme();
  const styles = getStyles(theme);
  const [open, setOpen] = useState(false);
  const touchedRef = useRef(false);
  const touchTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(touchTimerRef.current), []);
  const handleMouseEnter = () => {
    if (touchedRef.current) return;
    setOpen(true);
  };
  const handleMouseLeave = () => {
    if (touchedRef.current) return;
    setOpen(false);
  };
  const handleTouchEnd = () => {
    touchedRef.current = true;
    window.clearTimeout(touchTimerRef.current);
    touchTimerRef.current = window.setTimeout(() => {
      touchedRef.current = false;
    }, SYNTHETIC_MOUSE_EVENT_IGNORE_MS);
    setOpen(prev => !prev);
  };
  const rows: { label: string; value: string }[] = [
    { label: '予約変更手数料', value: detail.reservationChangeFee },
    { label: '払い戻し', value: detail.refund },
    { label: '機内手荷物', value: detail.carryOnBaggage },
    { label: '預け入れ手荷物', value: detail.checkedBaggage }
  ];
  const content = (
    <div className="tooltip__content" style={styles.content} data-testid="domestic-air-fare-tooltip-content">
      <div style={styles.header}>{detail.fareName}</div>
      {rows.map(row => (
        <div key={row.label} style={styles.row}>
          <span style={styles.label}>{row.label}</span>
          <span style={styles.value}>{row.value}</span>
        </div>
      ))}
      {detail.notes.length > 0 && (
        <div style={styles.notes}>
          {detail.notes.map(note => (
            <p key={note} style={styles.note}>
              {note}
            </p>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div
      style={styles.wrapper}
      onClick={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
      data-testid="domestic-air-fare-tooltip"
    >
      <StyledTooltip
        title={content}
        placement={place === 'left' ? 'left-start' : 'right-start'}
        open={open}
        disableHoverListener
        disableTouchListener
        disableFocusListener
      >
        <Icon
          help={false}
          className="tooltip__icon-info"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchEnd={handleTouchEnd}
        >
          i
        </Icon>
      </StyledTooltip>
    </div>
  );
};

/**
 * 「⚠事前座席指定不可」の注記（候補カード用）。
 * アイコンは黄色（#f5a623）、文字はデザイントークンの text.primary に合わせる。
 */
export const SeatSelectionRestrictedNote: React.FC = () => {
  const { theme } = useAITTheme();
  const styles = getStyles(theme);
  return (
    <div style={styles.seatNote} data-testid="seat-selection-restricted-note">
      <span style={styles.seatNoteIcon} aria-hidden="true">
        ⚠
      </span>
      <span style={styles.seatNoteText}>事前座席指定不可</span>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center'
  } as const,
  content: {
    fontSize: '11px',
    lineHeight: 1.5
  } as const,
  header: {
    fontWeight: 'bold',
    paddingBottom: '4px',
    marginBottom: '4px',
    borderBottom: '1px solid #cccccc'
  } as const,
  row: {
    display: 'flex',
    columnGap: '8px',
    padding: '1px 0'
  } as const,
  label: {
    flexShrink: 0,
    width: '90px',
    fontWeight: 'bold'
  } as const,
  value: {
    textAlign: 'left',
    wordBreak: 'break-all'
  } as const,
  notes: {
    marginTop: '4px',
    paddingTop: '4px',
    borderTop: '1px solid #cccccc'
  } as const,
  note: {
    margin: 0,
    fontSize: '10px',
    lineHeight: 1.5
  } as const,
  seatNote: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '2px',
    fontSize: '11px',
    lineHeight: 1.5
  } as const,
  seatNoteIcon: {
    color: '#f5a623'
  } as const,
  seatNoteText: {
    color: theme.tokens.colors.text.primary
  } as const
});

export default DomesticAirFareTooltip;
